import { postSurvey, getSurvey } from '@/service/custom-surveys'
import { curry, pipe, path } from 'ramda'

// initial state
function initialState() {
  return {
    id: null,
    customSurvey: {},
    survey: [],
    error: null,
    page: {
      current: undefined,
      total: undefined,
      loading: false,
    },
  }
}

const state = initialState()

// getters
const getters = {
  customSurvey: state => state.customSurvey,
  survey: state => state.survey,
  currentQuestion: state => state.survey[state.page.current - 1] || null,
  surveyPage: state => state.page,
  surveyError: state => state.error,
}

// actions
const actions = {
  cleanCustomSurvey({ commit }) {
    const clearState = initialState()
    commit('setUserFormSurveyId', clearState.id)
    commit('setCustomSurvey', clearState.customSurvey)
    commit('setSurvey', clearState.survey)
    commit('setPage', clearState.page)
  },

  async getSurvey({ commit }, id) {
    try {
      const {
        data: { customSurvey },
      } = await getSurvey(id)

      commit('setCustomSurvey', customSurvey)

      const hasUnfinishedSurvey = customSurvey.surveyAnswerId
      if (hasUnfinishedSurvey) {
        commit('setUserFormSurveyId', customSurvey.surveyAnswerId)
      }

      commit(
        'setSurvey',
        hasUnfinishedSurvey
          ? customSurvey.questions
          : customSurvey.questions.map(q => ({ ...q, answered: null }))
      )

      const page = customSurvey.currentQuestion + 1
      commit('setPage', {
        current:
          page > customSurvey.questions.length
            ? customSurvey.questions.length
            : page,
        total: customSurvey.questions.length,
      })

      commit('setError', null)
    } catch (error) {
      const wrapperCommit = (key, data) => commit(key, data)
      const setError = curry(wrapperCommit)('setError')
      pipe(
        path(['response']),
        setError
      )(error)
    }
  },

  async saveSurvey({ state, commit }) {
    return postSurvey(state.customSurvey.id, {
      customSurvey: {
        id: state.id,
        survey: state.survey,
        status: 'preview',
        page: state.page,
      },
    }).then(({ data: { customSurvey: { id } } }) =>
      commit('setUserFormSurveyId', id)
    )
  },

  async finishSurvey({ state, commit }) {
    const clearState = initialState()

    // avoid empty action
    if (state.survey.length === 0) {
      commit('setCustomSurvey', clearState.customSurvey)
      commit('setSurvey', clearState.survey)
      commit('setPage', clearState.page)
      return
    }

    let response = postSurvey(state.customSurvey.id, {
      customSurvey: {
        id: state.id,
        survey: state.survey.map(q => {
          return {
            question_id: q.question_id,
            answered: q.answered,
          }
        }),
        status: 'done',
      },
    })

    response.then(() => {
      commit('setCustomSurvey', clearState.customSurvey)
      commit('setSurvey', clearState.survey)
      commit('setPage', clearState.page)
    })

    return response
  },

  surveyPreviousPage({ commit }) {
    commit('surveyPreviousPage')
  },

  surveyNextPage({ commit }) {
    commit('surveyNextPage')
  },
}

// mutations
const mutations = {
  setUserFormSurveyId(state, id) {
    state['id'] = id
  },

  setCustomSurvey(state, customSurvey) {
    state['customSurvey'] = customSurvey
  },

  setSurvey(state, survey) {
    state.survey = survey
  },

  toggleSurveyPostLoading(state) {
    state.page.loading = !state.page.loading
  },

  setPage(state, page) {
    state.page = Object.assign(state.page, page)
  },

  surveyNextPage(state) {
    state.page.current++
  },

  selectAnswer(state, a) {
    if (!a) return

    const answer = JSON.parse(JSON.stringify(a))

    const isMulti = answer.hasOwnProperty('type') && answer.type === 'multi'

    const answered = {
      answer_id: answer.answer_id,
      ...(!isMulti && { score: answer.score }),
      ...(answer.text && { text: answer.text }),
      ...(answer.image && { image: answer.image }),
    }

    if (isMulti) {
      const _answered = state.survey[state.page.current - 1].answered || []
      const hasAnswered =
        _answered.filter(a => a.answer_id === answer.answer_id).length > 0

      if (hasAnswered) {
        // if exist in current > remove
        const filteredAnswered = _answered.filter(
          a => a.answer_id !== answer.answer_id
        )
        state.survey[state.page.current - 1].answered =
          filteredAnswered.length > 0 ? filteredAnswered : null
      } else {
        // if not exist in current > add
        state.survey[state.page.current - 1].answered = [..._answered, answer]
      }
    } else {
      const answeredId = (state.survey[state.page.current - 1].answered || {})
        .answer_id
      // if is current > unselect
      if (answeredId === answered.answer_id) {
        state.survey[state.page.current - 1].answered = null
      } else {
        // if not is current > select
        state.survey[state.page.current - 1].answered = answered
      }
    }
  },

  selectAnswerFeedback(state, feedback) {
    state.survey[state.page.current - 1].answered = feedback
  },

  surveyPreviousPage(state) {
    state.page.current--
  },

  setError(state, error) {
    state.error = error
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
